import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  Paper,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Collapse,
  Chip,
  Tooltip,
  // IconButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  // Download as DownloadIcon,
  // Print as PrintIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
// import { convertDateToWords } from "../utils/dateToWords";

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .split(' ')
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  name: { label: "Student's Name", defaultVisible: true },
  father: { label: "Father's Name", defaultVisible: true },
  mother: { label: "Mother's Name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  caste: { label: "Caste", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },
  rollNo: { label: "Roll No", defaultVisible: true },
  standard: { label: "Standard", defaultVisible: true },
};

export default function TermsMarksheetReceipt({ receiptData, school, setTemID }) {
  const student = receiptData.data || [];
  const receiptId = `marksheet`;

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#1976d2");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('termsMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  const [activities, setActivities] = useState([
    { name: "Formative Assessment-I" },
    { name: "Formative Assessment-II" },
    { name: "Final Exam" },
  ]);

  const totalGrade = [
    { name: "Semester" },
    { name: "Education" },
    { name: "Co-Curricular" },
    { name: "Total" },
    { name: "Special Remark" },
    { name: "C. Teacher Signature" },
  ];

  const totalActivity = [
    { name: "Qualities" },
    { name: "Creativity" },
    { name: "Regularity" },
    { name: "Punctuality" },
    { name: "Cleanness" },
    { name: "Cultural" },
    { name: "Discipline" },
    { name: "Confidence" },
    { name: "School Program" },
    { name: "School Mates " },
  ];

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('termsMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-terms';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 10mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-terms');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-terms');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  // extract unique subjects
  const subjects = useMemo(() => {
    const allMarks = student.flatMap((ms) => ms.marks || []);
    const unique = [...new Map(allMarks.map((m) => [m.subjectName?.trim(), m])).values()];
    return unique.sort((a, b) =>
      a.subjectName?.toLowerCase().localeCompare(b.subjectName?.toLowerCase())
    );
  }, [student]);

  // quick lookup
  const findMark = (examType, subject) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    return term?.marks?.find(
      (s) => s.subjectName?.trim().toLowerCase() === subject?.trim().toLowerCase()
    );
  };

  const getTotals = (examType) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  // const addActivity = () => {
  //   const newName = `Activity-${activities.length + 1}`;
  //   setActivities([...activities, { name: newName }]);
  // };

  const [terms, setTerms] = useState([
    { n: "First" },
    { n: "Second" },
    { n: "Final" }
  ]);

  const t1 = getTotals("First");
  const t2 = getTotals("Second");
  const t3 = getTotals("Final");

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const cell = { border: `1px solid ${borderColor}`, padding: "6px" };
  const cell1 = { border: `1px solid ${borderColor}`, padding: "2px 4px" };

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  // Get student data
  // const getStudentData = () => {
  //   return student.find((m) => m.examType?.trim().toLowerCase().includes('first')) || student[0] || {};
  // };

  // const studentData = getStudentData();

  // Render student info field
  // const renderStudentField = (fieldKey) => {
  //   if (!fieldVisibility[fieldKey]) return null;

  //   const field = STUDENT_INFO_FIELDS[fieldKey];
  //   let value = '';

  //   switch (fieldKey) {
  //     case 'apaarId':
  //       value = studentData?.apaarId || '';
  //       break;
  //     case 'name':
  //       value = toCamelCase(`${studentData?.name || ''} ${studentData?.last || ''}`.trim());
  //       break;
  //     case 'father':
  //       value = toCamelCase(`${studentData?.father || ''} ${studentData?.last || ''}`.trim());
  //       break;
  //     case 'mother':
  //       value = toCamelCase(`${studentData?.mother || ''} ${studentData?.last || ''}`.trim());
  //       break;
  //     case 'caste':
  //       value = studentData?.caste || '';
  //       break;
  //     case 'dob':
  //       value = studentData?.birthday ? new Date(studentData.birthday).toLocaleDateString("en-GB") : '';
  //       break;
  //     case 'dobInWords':
  //       value = studentData?.birthday ? convertDateToWords(studentData.birthday) : '';
  //       break;
  //     case 'photo':
  //       return studentData?.photo ? (
  //         <Box key={fieldKey} display="flex" justifyContent="center" my={0.5}>
  //           <img
  //             src={studentData.photo}
  //             alt="Student"
  //             style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
  //             onError={(e) => e.target.style.display = 'none'}
  //           />
  //         </Box>
  //       ) : null;
  //     case 'contact':
  //       value = studentData?.contact || studentData?.mobile || '';
  //       break;
  //     case 'address':
  //       value = studentData?.address || '';
  //       break;
  //     case 'rollNo':
  //       value = studentData?.rollNumber || studentData?.id || '';
  //       break;
  //     case 'standard':
  //       value = studentData?.classRoom?.standard || '';
  //       break;
  //     case 'section':
  //       value = studentData?.classRoom?.section || '';
  //       break;
  //     default:
  //       return null;
  //   }

  //   if (!value || value === '') return null;

  //   return (
  //     <Box
  //       key={fieldKey}
  //       sx={{
  //         display: "grid",
  //         gridTemplateColumns: "150px auto",
  //         mb: 0.3,
  //         gap: 0.5,
  //         alignItems: "center"
  //       }}
  //     >
  //       <Typography sx={{ fontWeight: "bold", fontSize: "0.8rem" }}>{field.label} :</Typography>
  //       <Typography sx={{ fontSize: "0.8rem" }}>{value}</Typography>
  //     </Box>
  //   );
  // };

  // const visibleFields = Object.keys(STUDENT_INFO_FIELDS).filter(key => fieldVisibility[key]);

  // Helper to render student info row
  // const renderStudentInfoRow = (fieldKey1, fieldKey2, fieldKey3) => {
  //   const fields = [fieldKey1, fieldKey2, fieldKey3].filter(f => f && fieldVisibility[f]);

  //   if (fields.length === 0) return null;

  //   return (
  //     <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
  //       {fields.map(fieldKey => {
  //         const field = STUDENT_INFO_FIELDS[fieldKey];
  //         let value = '';

  //         switch (fieldKey) {
  //           case 'apaarId':
  //             value = studentData?.apaarId || '';
  //             break;
  //           case 'name':
  //             value = toCamelCase(`${studentData?.name || ''} ${studentData?.last || ''}`.trim());
  //             break;
  //           case 'father':
  //             value = toCamelCase(`${studentData?.father || ''} ${studentData?.last || ''}`.trim());
  //             break;
  //           case 'mother':
  //             value = toCamelCase(`${studentData?.mother || ''} ${studentData?.last || ''}`.trim());
  //             break;
  //           case 'caste':
  //             value = studentData?.caste || '';
  //             break;
  //           case 'dob':
  //             value = studentData?.birthday ? new Date(studentData.birthday).toLocaleDateString("en-GB") : '';
  //             break;
  //           case 'dobInWords':
  //             value = studentData?.birthday ? convertDateToWords(studentData.birthday) : '';
  //             break;
  //           case 'contact':
  //             value = studentData?.contact || studentData?.mobile || '';
  //             break;
  //           case 'address':
  //             value = studentData?.address || '';
  //             break;
  //           case 'rollNo':
  //             value = studentData?.rollNumber || studentData?.id || '';
  //             break;
  //           case 'standard':
  //             value = studentData?.classRoom?.standard || '';
  //             break;
  //           case 'section':
  //             value = studentData?.classRoom?.section || '';
  //             break;
  //           default:
  //             return null;
  //         }

  //         if (!value || value === '') return null;

  //         return (
  //           <Typography key={fieldKey} variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
  //             <strong>{field.label}: </strong> {value}
  //           </Typography>
  //         );
  //       })}
  //     </Box>
  //   );
  // };

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: isLandscape ? "1100px" : "900px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: isLandscape ? "1100px" : "900px",
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" fontSize="0.7rem">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Page 1 - Marks Table */}
      <Box sx={{
        background: "#fff",
        fontFamily: "Arial",
        maxWidth: isLandscape ? "1100px" : "800px",
        margin: "auto",
        p: 2,
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "column", alignItems: "center", mb: 0.5 }}>
          <img
            src={school?.logoUrl}
            alt="School Logo"
            style={{ width: isLandscape ? 70 : 90, height: isLandscape ? 60 : 80, marginTop: 1 }}
            onError={(e) => e.target.style.display = 'none'}
          />

          <Typography variant={isLandscape ? "h6" : "h5"} align="center" fontWeight="bold" sx={{ color: borderColor }}>
            {toCamelCase(school?.schoolName)}
          </Typography>
          <Typography variant="subtitle2" align="center" fontSize={isLandscape ? "0.65rem" : "0.8rem"}>
            {toCamelCase([school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state].filter(Boolean).join(", "))}
          </Typography>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ fontWeight: "bold", my: 0.5, color: borderColor }}
          >
            PROGRESS REPORT SESSION - {student[0]?.academicYear}
          </Typography>
        </Box>
        <Divider sx={{ mb: 0.5, mt: 0.5, borderColor }} />

        <Box display="flex" justifyContent="center" my={0.5}>
          <img
            src={student[0]?.photo}
            alt="Student"
            style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
            onError={(e) => e.target.style.display = 'none'}
          />
        </Box>

        {/* Student Info - Using visibleFields */}
        <Box sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
          {/* Row 1: Name, Mother, Standard */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
            {fieldVisibility.name && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Student's Name: </strong> {toCamelCase(`${[student[0]?.name, student[0]?.last].filter(Boolean).join(" ")}`)}
              </Typography>
            )}
            {fieldVisibility.mother && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Mother's Name: </strong> {toCamelCase(`${[student[0]?.mother, student[0]?.last].filter(Boolean).join(" ")}`)}
              </Typography>
            )}
            {fieldVisibility.standard && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Standard: </strong>{student[0]?.classRoom?.standard} | {student[0]?.classRoom?.section}
              </Typography>
            )}
          </Box>

          {/* Row 2: Father, Apaar ID, Roll No */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
            {fieldVisibility.father && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Father's Name: </strong> {toCamelCase(`${[student[0]?.father, student[0]?.last].filter(Boolean).join(" ")}`)}
              </Typography>
            )}
            {fieldVisibility.apaarId && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Apaar Id: </strong> {student[0]?.apaarId}
              </Typography>
            )}
            {fieldVisibility.rollNo && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Roll No.: </strong> {student[0]?.rollNumber}
              </Typography>
            )}
          </Box>

          {/* Row 3: Additional fields - Caste, DOB, Contact, etc. */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
            {fieldVisibility.caste && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Caste: </strong>{[student[0]?.category, student[0]?.caste].filter(Boolean).join(" | ")}
              </Typography>
            )}
            {fieldVisibility.dob && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>DOB: </strong> {student[0]?.birthday ? new Date(student[0].birthday).toLocaleDateString("en-GB") : ''}
              </Typography>
            )}
            {fieldVisibility.contact && (
              <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                <strong>Mob.: </strong> {student[0]?.mobile || ''}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider sx={{ m: 0.5, borderColor }} />

        {/* Marks Table */}
        <Box sx={{ py: 1, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} rowSpan={2} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Subject</strong>
                </TableCell>

                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} colSpan={4} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Term-I</strong>
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} colSpan={4} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Term-II</strong>
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} colSpan={4} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Final Exam</strong>
                </TableCell>
              </TableRow>

              <TableRow>
                {[...Array(3)].flatMap(() => [
                  <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Max</TableCell>,
                  <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Min</TableCell>,
                  <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Obt</TableCell>,
                  <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Grade</TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => {
                const term1 = findMark("First", sub.subjectName);
                const term2 = findMark("Second", sub.subjectName);
                const finalExam = findMark("Final", sub.subjectName);

                return (
                  <TableRow key={i}>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                      {toCamelCase(sub.subjectName)}
                    </TableCell>

                    {/* Term-I */}
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term1?.maxMark || 100}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{Math.round((term1?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term1?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term1?.grade || "-"}</TableCell>

                    {/* Term-II */}
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term2?.maxMark || 100}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{Math.round((term2?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term2?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{term2?.grade || "-"}</TableCell>

                    {/* Final Exam */}
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{finalExam?.maxMark || 100}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{Math.round((finalExam?.maxMark || 100) * 34 / 100)}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{finalExam?.marksObtained || "-"}</TableCell>
                    <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{finalExam?.grade || "-"}</TableCell>
                  </TableRow>
                );
              })}

              {/* Totals Row */}
              <TableRow sx={{ background: alpha(borderColor, 0.06) }}>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }}><strong style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Total Marks</strong></TableCell>

                {/* Term-I */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.grade}</TableCell>

                {/* Term-II */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.grade}</TableCell>

                {/* Final Exam */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "8px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.grade}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
            <strong>Result:</strong> {student[2]?.result || 'PASS'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
            <strong>Remarks:</strong> {student[2]?.remarks || 'Excellent Performance'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
            <strong>Percentage:</strong> {student[2]?.percentage || '87'}%
          </Typography>
        </Box>

        <Divider sx={{ mb: 0.5, borderColor }} />

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Class Teacher: ______________</Typography>
          <Typography variant="body2" sx={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>Head Master: ______________</Typography>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      {/* Page 2 - Co-Curricular Activities */}
      <Box sx={{
        maxWidth: isLandscape ? "1100px" : "800px",
        margin: "auto",
        p: 2,
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}>
        {/* Co-Curricular Activity Table */}
        <Box sx={{ width: '100%', mb: 1, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} colSpan={activities.length + 1} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Co-Curricular Activity</strong>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.75rem" }}>{'#'}</TableCell>
                {activities.map((a, i) => (
                  <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} key={i} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.75rem" }}>
                    {a.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {totalActivity.map((sub, i) => {
                return (
                  <TableRow key={i}>
                    <TableCell sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }} style={{ fontSize: isLandscape ? "0.65rem" : "0.75rem" }}>{toCamelCase(sub.name)}</TableCell>
                    {activities.map((_, j) => (
                      <TableCell sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }} align="center" key={j}>
                        <TextField
                          variant="standard"
                          placeholder="Very Good"
                          slotProps={{
                            input: {
                              disableUnderline: true,
                            },
                          }}
                          sx={{
                            "& input": {
                              fontFamily: "inherit",
                              fontSize: isLandscape ? "0.65rem" : "0.75rem",
                              padding: "2px 4px",
                            },
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Total Grade Table */}
        <Box sx={{ width: '100%', mb: 1, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} colSpan={totalGrade.length + 1} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }}>Total Grade</strong>
                </TableCell>
              </TableRow>

              <TableRow>
                {totalGrade.map((a, j) => (
                  <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} key={j} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.75rem" }}>
                    {a.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => {
                return (
                  <TableRow key={i}>
                    {i < 3 && totalGrade.map((_, j) => (
                      <TableCell key={j} sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }}>
                        {j === 0 ? (
                          terms[i]?.n || "-"
                        ) : (
                          <TextField
                            variant="standard"
                            placeholder=".........."
                            slotProps={{ input: { disableUnderline: true } }}
                            sx={{
                              "& input": {
                                fontSize: isLandscape ? "0.65rem" : "0.75rem",
                                padding: "2px 4px",
                              },
                            }}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <TextField
          sx={{
            mt: 0.5,
            width: '100%',
            "& .MuiInputBase-input": {
              fontSize: isLandscape ? "0.75rem" : "0.85rem",
            },
          }}
          variant="standard"
          placeholder="Very Good"
          slotProps={{
            input: { disableUnderline: true },
          }}
        />

        <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "space-around", my: 1 }}>
          <Typography variant="body2" fontSize={isLandscape ? "0.7rem" : "0.8rem"}>Class Teacher sign </Typography>
          <Typography variant="body2" fontSize={isLandscape ? "0.7rem" : "0.8rem"}>Head Master </Typography>
        </Box>
      </Box>
    </Box>
  );
}